import * as React from 'react';
import { UserAgentApplication } from 'msal';
import { apiConfig, config } from './Config';
import { getUserDetails, getGroups } from '../graph/GraphService';
import { Group } from 'microsoft-graph';

export interface AuthComponentProps {
    error: any;
    isAuthenticated: boolean;
    user: any;
    login: Function;
    logout: Function;
    getAccessToken: Function;
    setError: Function;
}

interface AuthProviderState {
    error: any;
    isAuthenticated: boolean;
    user: any;
}

export default function withAuthProvider<T extends React.Component<AuthComponentProps>>
    (WrappedComponent: new (props: AuthComponentProps, context?: any) => T): React.ComponentClass {
    return class extends React.Component<any, AuthProviderState> {

        private userAgentApplication: UserAgentApplication;
        private authRedirectInProgress: boolean;

        private getCurrentAccount = () => {
            return this.userAgentApplication.getAccount() || this.userAgentApplication.getAllAccounts()[0];
        }

        private getLoginScopes = (): string[] => {
            return Array.from(new Set([...(config.scopes || []), ...(apiConfig.resourceScope || [])]));
        }

        private getUserFromAccount = (account: any) => {
            return {
                displayName: account?.name || account?.idToken?.claims?.name || account?.userName || "",
                email: account?.userName || account?.idToken?.claims?.upn || ""
            };
        }

        private hasRequiredRoleClaim = (account: any): boolean => {
            const claims = account?.idToken?.claims || account?.idTokenClaims || {};
            const roles: string[] = claims.roles || [];
            return roles.includes("GDLWriters") || roles.includes("GDLReaders");
        }

        private clearMsalBrowserCache = () => {
            const clearStorage = (storage: Storage) => {
                const keysToRemove: string[] = [];

                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (key && key.toLowerCase().indexOf("msal") > -1) {
                        keysToRemove.push(key);
                    }
                }

                keysToRemove.forEach((key) => storage.removeItem(key));
            };

            clearStorage(window.localStorage);
            clearStorage(window.sessionStorage);
        }

        constructor(props: any) {
            super(props);
            this.state = {
                error: null,
                isAuthenticated: false,
                user: {}
            };
            this.authRedirectInProgress = false;

            // Initialize the MSAL application object
            this.userAgentApplication = new UserAgentApplication({
                auth: {
                    authority: config.authority,
                    clientId: config.clientId,
                    redirectUri: config.redirectUri,
                    validateAuthority: config.validateAuthority,
                    navigateToLoginRequestUrl: config.navigateToLoginRequestUrl
                },
                cache: {
                    cacheLocation: "localStorage",
                    storeAuthStateInCookie: true
                },
                system: {
                    tokenRenewalOffsetSeconds: 60,
                    loadFrameTimeout: 20000,
                }
            });

            // Ensure redirect responses are processed so account/token cache is populated.
            // The response may already contain an access token (from loginRedirect or acquireTokenRedirect);
            // using it directly avoids a second silent/interactive token call and the extra page reload.
            this.userAgentApplication.handleRedirectCallback((error, response) => {
                this.authRedirectInProgress = false;

                if (error) {
                    this.setState({
                        isAuthenticated: false,
                        user: {},
                        error: this.normalizeError(error)
                    });
                    return;
                }

                const account = this.getCurrentAccount();
                if (account) {
                    // Pass the access token from the redirect response when available
                    // so getUserProfile doesn't need to acquire another token (which would
                    // cause a second page reload via acquireTokenRedirect).
                    const token = response && response.accessToken ? response.accessToken : undefined;
                    this.getUserProfile(token);
                }
            });
        }

        componentDidMount() {
            // If MSAL already has an account, the user
            // is already logged in

            console.log("Config setup for env : " + process.env.REACT_APP_ENV);

            let account = this.getCurrentAccount();

            if (account) {
                // Enhance user object with data from Graph
                this.getUserProfile();
            }
        }

        render() {
            return <WrappedComponent
                error={this.state.error}
                isAuthenticated={this.state.isAuthenticated}
                user={this.state.user}
                login={() => this.login()}
                logout={() => this.logout()}
                getAccessToken={(scopes: string[]) => this.getAccessToken(scopes)}
                setError={(message: string, debug: string) => this.setErrorMessage(message, debug)}
                {...this.props} />;
        }

        public login = async () => {
            if (this.authRedirectInProgress) {
                return;
            }

            try {
                // Reset stale MSAL browser cache before explicit sign-in.
                this.clearMsalBrowserCache();
                this.authRedirectInProgress = true;
                await this.userAgentApplication.loginRedirect(
                    {
                        scopes: this.getLoginScopes(),
                        prompt: "select_account"
                    });
            }
            catch (err) {
                this.authRedirectInProgress = false;
                this.setState({
                    isAuthenticated: false,
                    user: {},
                    error: this.normalizeError(err)
                });
            }
        }

        public logout = async () => {
            this.userAgentApplication.logout();
        }

        private getAccessToken = async (scopes: string[], useremail?: string): Promise<string> => {
            const account = this.getCurrentAccount();

            if (!account) {
                await this.login();
                return "";
            }

            try {
                // Get the access token silently
                // If the cache contains a non-expired token, this function
                // will just return the cached token. Otherwise, it will
                // make a request to the Azure OAuth endpoint to get a token
                let silentResult = await this.userAgentApplication.acquireTokenSilent({
                    scopes: scopes
                    , account
                });

                return silentResult.accessToken;

            } catch (err) {
                // If a silent request fails, it may be because the user needs
                // to login or grant consent to one or more of the requested scopes

                console.log(" Error : " + err);
                console.log("Interection req > called. scopes: " + JSON.stringify(scopes));

                if (this.isInteractionRequired(err as Error)) {
                    if (!this.authRedirectInProgress) {
                        this.authRedirectInProgress = true;
                        await this.userAgentApplication.acquireTokenRedirect({
                            scopes: scopes
                        });
                    }

                    return "";
                }

                // Silent renewal timeout is common on first load; force interactive token flow.
                if (!this.authRedirectInProgress) {
                    this.authRedirectInProgress = true;
                    await this.userAgentApplication.acquireTokenRedirect({
                        scopes: scopes
                    });
                }

                return "";

            }
        }


        private getUserProfile = async (existingToken?: string) => {
            try {
                const account = this.getCurrentAccount();
                const claimUser = this.getUserFromAccount(account);

                // Prefer app role claims already present in the token and skip Graph when possible.
                if (account && this.hasRequiredRoleClaim(account)) {
                    this.setState({
                        isAuthenticated: true,
                        user: claimUser,
                        error: null
                    });
                    return;
                }

                let accessToken = existingToken || await this.getAccessToken(config.scopes);
                if (accessToken) {
                    let user = claimUser;
                    let groups: Group[] = [];

                    // Graph calls are best-effort; if Graph denies the token, do not crash startup.
                    try {
                        const userGraph = await getUserDetails(accessToken);
                        user = {
                            displayName: userGraph.displayName || claimUser.displayName,
                            email: userGraph.mail || userGraph.userPrincipalName || claimUser.email
                        };
                    } catch (err) {
                        console.warn("Graph /me call failed", err);
                    }

                    try {
                        const groupsAll = await getGroups(accessToken);
                        groups = groupsAll.value || [];
                    } catch (err) {
                        console.warn("Graph /me/memberOf call failed", err);
                    }

                    const hasGraphGroupAccess = groups.findIndex((x: Group) => x.displayName && config.groupClaims.findIndex((g: string) => g === x.displayName!) !== -1) !== -1;

                    // Graph can intermittently return 401 during startup; do not block app shell on that.
                    if (hasGraphGroupAccess || !!account) {

                        this.setState({
                            isAuthenticated: true,
                            user,
                            error: null
                        });

                    } else {

                        this.setState({
                            isAuthenticated: false,
                            user,
                            error: {
                                message: "You dont have access to this application, please contact helpdesk or administrator.",
                                debug: "Authentication failed, User is not belong to - GDL Enterprise App Security Group."
                            }
                        });

                    }


                }
            }
            catch (err) {
                this.setState({
                    isAuthenticated: false,
                    user: {},
                    error: this.normalizeError(err)
                });
            }
        }


        public setErrorMessage = (message: string, debug: string) => {
            this.setState({
                error: { message: message, debug: debug }
            });
        }

        public normalizeError = (error: any): any => {
            let normalizedError = {};
            if (typeof (error) === 'string') {
                let errParts = error.split('|');
                normalizedError = errParts.length > 1 ?
                    { message: errParts[1], debug: errParts[0] } :
                    { message: error };
            } else if (error instanceof Error) {
                normalizedError = {
                    message: error.message,
                    debug: JSON.stringify(error)
                };
            } else {
                normalizedError = {
                    message: "Unknown error!",
                    debug: JSON.stringify(error)
                };
            }
            return normalizedError;
        }

        public isInteractionRequired = (error: Error): boolean => {
            if (!error.message || error.message.length <= 0) {
                return false;
            }

            return (
                error.message.indexOf('consent_required') > -1 ||
                error.message.indexOf('interaction_required') > -1 ||
                error.message.indexOf('login_required') > -1 ||
                error.message.indexOf('popup_window_error') > -1 ||
                error.message.indexOf('token_renewal_error') > -1 ||
                error.message.indexOf('Token renewal operation failed') > -1
            );
        }
    }
}
